// ═══════════════════════════════════════════════════════════════
// QuickTaskModal — Create or edit a director task
// Path: src/components/features/QuickTaskModal.jsx
//
// Used from TasksView (new task / edit) and DonorDetailSidebar
// (quick-add with pre-linked donor + director).
//
// Recurrence surface: the Repeats/Ends block drives creation of a
// task_recurrences template. Editing a recurring task instance
// defaults to "this occurrence only"; admins can toggle into
// "edit the whole series" to update the template for future spawns.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import { ClipboardCheck, RotateCw, Pencil } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { taskService } from "../../services/taskService";
import { Modal } from "../ui/Modal";
import { ActionButton } from "../ui/ActionButton";
import { FormSelect } from "../ui/FormControls";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import {
  TASK_PRIORITIES,
  TASK_CATEGORIES,
  TASK_FREQUENCIES,
  TASK_END_TYPES,
} from "../../constants/taskTypes";

export const QuickTaskModal = ({
  onClose,
  onSave,
  editingTask = null,
  directors = [],
  members = [],
  defaultDirector = "",
  defaultMemberId = null,
}) => {
  const { palette, isAdmin, updateSeries } = useApp();
  const isEdit = !!editingTask;
  const editingRecurrenceId = editingTask?.recurrenceId || null;

  // Occurrence-level state — what the user sees for a single task row
  const [title, setTitle] = useState(editingTask?.title || "");
  const [category, setCategory] = useState(editingTask?.category || "general");
  const [priority, setPriority] = useState(editingTask?.priority || "normal");
  const [dueDate, setDueDate] = useState(editingTask?.dueDate || "");
  const [notes, setNotes] = useState(editingTask?.notes || "");
  const [directorName, setDirectorName] = useState(
    editingTask?.directorName || defaultDirector || ""
  );
  const [memberId, setMemberId] = useState(
    editingTask?.memberId || defaultMemberId || ""
  );

  // Recurrence rule state (only used at CREATE time unless edit-series is on)
  const [frequency, setFrequency] = useState("none");
  const [intervalValue, setIntervalValue] = useState(1);
  const [endType, setEndType] = useState("never");
  const [endCount, setEndCount] = useState(5);
  const [endDate, setEndDate] = useState("");

  // Edit-series mode (admin only, when editing a recurring instance)
  const [seriesMode, setSeriesMode] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // When admin toggles into series mode for an existing recurring task,
  // fetch the recurrence template and hydrate the recurrence + content
  // fields from it so edits apply to the template, not the instance.
  useEffect(() => {
    let cancelled = false;
    const loadSeries = async () => {
      if (!seriesMode || !editingRecurrenceId) return;
      setSeriesLoading(true);
      try {
        const rec = await taskService.getRecurrence(editingRecurrenceId);
        if (cancelled || !rec) return;
        setTitle(rec.title || "");
        setCategory(rec.category || "general");
        setPriority(rec.priority || "normal");
        setNotes(rec.notes || "");
        setDirectorName(rec.directorName || "");
        setMemberId(rec.memberId || "");
        setFrequency(rec.frequency || "none");
        setIntervalValue(rec.intervalValue || 1);
        setEndType(rec.endType || "never");
        setEndCount(rec.endCount || 5);
        setEndDate(rec.endDate || "");
      } finally {
        if (!cancelled) setSeriesLoading(false);
      }
    };
    loadSeries();
    return () => {
      cancelled = true;
    };
  }, [seriesMode, editingRecurrenceId]);

  const handleSubmit = async () => {
    if (!title.trim()) return;

    // Edit-series mode writes to the recurrence template only.
    if (seriesMode && editingRecurrenceId) {
      await updateSeries(editingRecurrenceId, {
        title: title.trim(),
        notes: notes.trim(),
        category,
        priority,
        memberId: memberId || null,
        directorName: directorName || "",
        frequency,
        intervalValue: Math.max(1, parseInt(intervalValue, 10) || 1),
        endType,
        endCount: endType === "count" ? parseInt(endCount, 10) || null : null,
        endDate: endType === "date" ? endDate || null : null,
      });
      onClose();
      return;
    }

    // Occurrence save (create or edit single instance). On create,
    // pass recurrence info if frequency != "none".
    const payload = {
      title: title.trim(),
      category,
      priority,
      dueDate: dueDate || null,
      notes: notes.trim(),
      directorName: directorName || "",
      memberId: memberId || null,
    };
    if (!isEdit && frequency !== "none") {
      payload.recurrence = {
        frequency,
        intervalValue: Math.max(1, parseInt(intervalValue, 10) || 1),
        endType,
        endCount: endType === "count" ? parseInt(endCount, 10) || null : null,
        endDate: endType === "date" ? endDate || null : null,
      };
    }
    onSave(payload);
  };

  const donorOptions = [
    { value: "", label: "— No linked donor —" },
    ...members
      .slice(0, 200)
      .map((m) => ({
        value: m.id,
        label:
          `${m.firstName || ""} ${m.lastName || ""}`.trim() ||
          m.organization ||
          m.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const labelStyle = {
    fontSize: FONT_SIZES.xs,
    fontWeight: 600,
    color: palette.text2,
    fontFamily: FONT_FAMILY,
    display: "block",
    marginBottom: 4,
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    background: palette.inputBg,
    color: palette.inputColor,
    border: `1px solid ${palette.inputBorder}`,
    outline: "none",
  };

  const modalTitle = seriesMode
    ? "Edit Series Template"
    : isEdit
    ? "Edit Task"
    : "New Task";

  const showRecurrenceControls = !isEdit || seriesMode;

  return (
    <Modal title={modalTitle} icon={ClipboardCheck} onClose={onClose} width={480}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Recurring indicator + edit-series toggle */}
        {isEdit && editingRecurrenceId && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "8px 10px",
              borderRadius: 8,
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FONT_SIZES.xs,
                color: palette.text2,
              }}
            >
              <RotateCw size={14} style={{ color: palette.text3 }} />
              {seriesMode
                ? "Editing future occurrences — current and past rows stay as-is."
                : "Part of a repeating series. Changes here affect only this occurrence."}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setSeriesMode(!seriesMode)}
                disabled={seriesLoading}
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: palette.accent1,
                  background: "none",
                  border: "none",
                  cursor: seriesLoading ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: 0,
                }}
                title={
                  seriesMode
                    ? "Switch back to editing just this occurrence"
                    : "Edit the series template (affects future spawns)"
                }
              >
                <Pencil size={12} />
                {seriesMode ? "Edit occurrence" : "Edit series"}
              </button>
            )}
          </div>
        )}

        {/* Title */}
        <div>
          <label style={labelStyle}>Title *</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            autoFocus
            style={inputStyle}
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim()) handleSubmit();
            }}
          />
        </div>

        {/* Row: Category + Priority */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Category</label>
            <FormSelect
              value={category}
              onChange={setCategory}
              options={TASK_CATEGORIES.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Priority</label>
            <FormSelect
              value={priority}
              onChange={setPriority}
              options={TASK_PRIORITIES.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </div>
        </div>

        {/* Row: Due Date + Director. Due Date hidden in series mode —
            each instance has its own due date computed from frequency. */}
        <div style={{ display: "flex", gap: 12 }}>
          {!seriesMode && (
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Director</label>
            <FormSelect
              value={directorName}
              onChange={setDirectorName}
              options={[
                { value: "", label: "— Select —" },
                ...(directors || []).map((d) => ({
                  value: d.name,
                  label: d.name,
                })),
              ]}
            />
          </div>
        </div>

        {/* Linked donor (hidden if pre-bound via defaultMemberId) */}
        {!defaultMemberId && (
          <div>
            <label style={labelStyle}>Linked Donor</label>
            <FormSelect
              value={memberId}
              onChange={setMemberId}
              options={donorOptions}
            />
          </div>
        )}

        {/* Recurrence: Repeats + Ends. Shown at create time and in
            series-edit mode. Hidden when editing a single occurrence. */}
        {showRecurrenceControls && (
          <>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Repeats</label>
                <FormSelect
                  value={frequency}
                  onChange={setFrequency}
                  options={TASK_FREQUENCIES.map((f) => ({
                    value: f.id,
                    label: f.name,
                  }))}
                />
              </div>
              {frequency === "custom" && (
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Every N days</label>
                  <input
                    type="number"
                    min={1}
                    value={intervalValue}
                    onChange={(e) => setIntervalValue(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            {frequency !== "none" && (
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Ends</label>
                  <FormSelect
                    value={endType}
                    onChange={setEndType}
                    options={TASK_END_TYPES.map((e) => ({
                      value: e.id,
                      label: e.name,
                    }))}
                  />
                </div>
                {endType === "count" && (
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Occurrences</label>
                    <input
                      type="number"
                      min={1}
                      value={endCount}
                      onChange={(e) => setEndCount(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                )}
                {endType === "date" && (
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Stop on</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Notes */}
        <div>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional details..."
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 4,
          }}
        >
          <ActionButton
            label="Cancel"
            variant="secondary"
            onClick={onClose}
            small
          />
          <ActionButton
            label={
              seriesMode
                ? "Save Series"
                : isEdit
                ? "Save Changes"
                : "Create Task"
            }
            icon={ClipboardCheck}
            onClick={handleSubmit}
            disabled={!title.trim() || seriesLoading}
            small
          />
        </div>
      </div>
    </Modal>
  );
};
